import cn from "classnames";
import Select from "components/shared/select";
import { chatTypesEnum } from "helpers/enums";
import Tooltip from "rc-tooltip";
import React, { Fragment, useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Popover } from "react-tiny-popover";
import { getBidParticipants } from "sources/books/bids";
import { list as employeesList } from "sources/books/employees";
import {
    addGroupUsersToDriverChat,
    addUserToChat,
    deleteGroupUsersFromDriverChat,
    deleteUserFromChat,
    leaveChat,
} from "sources/messages";

const USERS_COUNT_IN_LIST = 2;

const chatUserSelectStyles = {
    container: (styles) => ({ ...styles, width: 240 }),
    menuPortal: (styles) => ({ ...styles, zIndex: 1070 }),
};

const userNameKeys = (fullName) =>
    (fullName || "")
        .split(" ")
        .slice(0, 2)
        .reduce((acc, name) => `${acc}${name[0] || ""}`, "");

const getUserName = (user) => user.fullName || user.displayName || "";

const ChatUserManager = ({ user, chatId, updateChatInfo, inChat, groupId, setLoading, canChangeUsers }) => {
    const userName = getUserName(user);

    const canDeleteUser = canChangeUsers && user.canBeDeleted;

    const action = () => {
        setLoading(true);
        const params = { chatId, userId: user.id };
        const requestPromise = groupId
            ? inChat
                ? deleteGroupUsersFromDriverChat({ ...params, groupId })
                : addGroupUsersToDriverChat({ ...params, groupId })
            : inChat
              ? deleteUserFromChat(params)
              : addUserToChat(params);

        requestPromise
            .then((res) => {
                if (res) {
                    return updateChatInfo();
                }
                return null;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div key={user.id} className="chat-participant__manage-item">
            <div className="chat-participant ml-0">{userNameKeys(userName)}</div>
            <div className="chat-participant__text px-2 ellipsis" title={userName}>
                {userName}
            </div>
            <div className="flex-1 text-right">
                {!user.isDriver && (!inChat || canDeleteUser) && (
                    <button
                        className={cn("btn btn-xs chat-participant__manage-item--action", {
                            "btn-danger": inChat,
                            "btn-success": !inChat,
                        })}
                        onClick={action}
                        type="button"
                    >
                        <b>{inChat ? <>&times;</> : "+"}</b>
                    </button>
                )}
            </div>
        </div>
    );
};

const AddChatUser = ({ chatId, usersInChatState, updateChatInfo, setLoading }) => {
    const [userId, setUserId] = useState(null);

    const addUser = () => {
        if (!userId) {
            return;
        }
        setLoading(true);
        addUserToChat({ chatId, userId })
            .then((res) => {
                if (res) {
                    setUserId(null);
                    return updateChatInfo();
                }
                return null;
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="d-flex align-items-center mb-2">
            <Select
                inputProps={{ name: "chatUserId" }}
                onChange={({ value }) => setUserId(value)}
                value={userId}
                url={employeesList}
                propName="fullNameOrEmail"
                filterPropName="fullNameOrEmail"
                placeholder="Выберите пользователя"
                minimumInputLength={2}
                excludeItems={Object.keys(usersInChatState).map(Number)}
                menuPortalTarget={document.body}
                styles={chatUserSelectStyles}
            />
            <button className="btn btn-sm btn-primary ml-2" disabled={!userId} onClick={addUser} type="button">
                Добавить
            </button>
        </div>
    );
};

export default function Participants(props) {
    const [bidUsers, setBidUsers] = useState({ users: [], groups: [] });
    const [loading, setLoading] = useState(false);
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [usersInChatState, setUsersInChatState] = useState(
        props.users.reduce((acc, item) => {
            acc[item.id] = true;
            return acc;
        }, {}),
    );

    useEffect(() => {
        if (props.bidId && props.type === chatTypesEnum.DriverChat) {
            getBidParticipants(props.bidId).then((data) => {
                const newUsersInChatState = { ...usersInChatState };
                const addUser = (item) => {
                    newUsersInChatState[item.id] ??= false;
                };

                data.users.forEach(addUser);
                data.groups?.forEach((group) => {
                    group.users.forEach(addUser);
                });
                props.users.forEach((user) => {
                    if (!data.users.some((u) => u.id === user.id)) {
                        data.users.push({ id: user.id, displayName: user.fullName });
                    }
                    addUser(user);
                });
                setUsersInChatState(newUsersInChatState);
                setBidUsers(data);
            });
        }
    }, [props.bidId]);

    useEffect(() => {
        setUsersInChatState(
            props.users.reduce((acc, item) => {
                acc[item.id] = true;
                return acc;
            }, {}),
        );
    }, [props.users]);

    const isDriverChat = props.type === chatTypesEnum.DriverChat;
    const isBidChat = props.type === chatTypesEnum.BidChat;

    if (!isDriverChat && !isBidChat) {
        return null;
    }
    const usersToShowing = props.users.slice(
        0,
        props.users.length === USERS_COUNT_IN_LIST + 1 ? USERS_COUNT_IN_LIST + 1 : USERS_COUNT_IN_LIST,
    );
    const moreUsersCount = props.users.length - usersToShowing.length;
    const moreUsersOverlay = () => (
        <div className="rc-tooltip__unwrap">
            <Scrollbars autoHeight autoHeightMin={100} autoHeightMax={300}>
                <div className="rc-tooltip__wrap">
                    {props.users.slice(usersToShowing.length).map((item, i, arr) => (
                        <div key={item.id} className={cn("d-flex", { "pb-2": i !== arr.length - 1 })}>
                            <div className={cn("chat-participant ml-0", { "chat-participant--online": item.isOnline })}>
                                {userNameKeys(item.fullName) || <i className="icon-uEA02-avatar" />}
                            </div>
                            <div className="chat-participant__text pl-2 ellipsis" title={item.fullName}>
                                {item.fullName}
                            </div>
                        </div>
                    ))}
                </div>
            </Scrollbars>
        </div>
    );

    const leaveFromChatHandler = () => {
        props.modal({
            name: "confirm",
            title: "Вы уверены, что хотите выйти из чата?",
            text: "",
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => leaveChat({ chatId: props.chatId }).then((res) => res && props.back()),
        });
    };

    const popoverContent = () => {
        return (
            <div className={cn("popover p-2 popover__overflow w-auto", { "loading loading--small": loading })}>
                {isBidChat && props.info?.canChangeUsers && (
                    <AddChatUser
                        chatId={props.chatId}
                        usersInChatState={usersInChatState}
                        setLoading={setLoading}
                        updateChatInfo={() => props.updateChatInfo()}
                    />
                )}
                <div className="chat-participant__users-list-title">Участники чата:</div>
                {(isBidChat ? props.users : bidUsers.users)?.map((item) => (
                    <ChatUserManager
                        key={item.id}
                        user={item}
                        chatId={props.chatId}
                        inChat={usersInChatState[item.id]}
                        setLoading={setLoading}
                        updateChatInfo={() => props.updateChatInfo()}
                        canChangeUsers={isDriverChat || props.info?.canChangeUsers}
                    />
                ))}
                {isDriverChat &&
                    bidUsers.groups?.map((group) => {
                        const somebodyInGroup = group.users.some((user) => usersInChatState[user.id]);
                        const somebodyOutGroup = group.users.some((user) => !usersInChatState[user.id]);
                        const addAction = () => {
                            setLoading(true);
                            addGroupUsersToDriverChat({ chatId: props.chatId, groupId: group.groupId })
                                .then((res) => (res ? props.updateChatInfo() : null))
                                .finally(() => setLoading(false));
                        };
                        const deleteAction = () => {
                            setLoading(true);
                            deleteGroupUsersFromDriverChat({ chatId: props.chatId, groupId: group.groupId })
                                .then((res) => (res ? props.updateChatInfo() : null))
                                .finally(() => setLoading(false));
                        };
                        return (
                            <Fragment key={group.groupId}>
                                <div className="chat-participant__manage-item pt-3">
                                    <div className="ellipsis text-muted" title={group.name}>
                                        {group.name}
                                    </div>
                                    <div className="d-flex gap-1">
                                        {somebodyOutGroup && (
                                            <Tooltip overlay="Добавить всех участников группы в чат">
                                                <button
                                                    className={cn(
                                                        "btn btn-xs chat-participant__manage-item--action btn-success",
                                                    )}
                                                    onClick={addAction}
                                                    type="button"
                                                >
                                                    <b>+</b>
                                                </button>
                                            </Tooltip>
                                        )}
                                        {somebodyInGroup && (
                                            <Tooltip overlay="Удалить всех участников группы из чата">
                                                <button
                                                    className={cn(
                                                        "btn btn-xs chat-participant__manage-item--action btn-danger",
                                                    )}
                                                    onClick={deleteAction}
                                                    type="button"
                                                >
                                                    <b>&times;</b>
                                                </button>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                                {group.users.map((item) => (
                                    <ChatUserManager
                                        key={item.id}
                                        user={item}
                                        chatId={props.chatId}
                                        inChat={usersInChatState[item.id]}
                                        groupId={group.groupId}
                                        setLoading={setLoading}
                                        updateChatInfo={() => props.updateChatInfo()}
                                        canChangeUsers={isDriverChat || props.info?.canChangeUsers}
                                    />
                                ))}
                            </Fragment>
                        );
                    })}
            </div>
        );
    };

    const closePopoverOnClickOutside = (e) => {
        if (e.target.closest(".react-select__menu")) {
            return;
        }
        setPopoverOpened(false);
    };

    return (
        <div className="chat-participant__grid">
            {usersToShowing.map((item) => (
                <Tooltip key={item.id} placement="bottomRight" overlay={item.fullName || `Пользователь #${item.id}`}>
                    <div className={cn("chat-participant", { "chat-participant--online": item.isOnline })}>
                        {userNameKeys(item.fullName) || <i className="icon-uEA02-avatar" />}
                    </div>
                </Tooltip>
            ))}
            {moreUsersCount > 0 && (
                <Tooltip
                    placement="bottomRight"
                    overlay={moreUsersOverlay()}
                    overlayClassName="chat-participant__more-users"
                >
                    <div className="chat-participant chat-participant--solid">+{moreUsersCount}</div>
                </Tooltip>
            )}
            {(isBidChat ? props.info?.canChangeUsers : bidUsers.users?.length > 0 || bidUsers.groups?.length > 0) && (
                <Popover
                    isOpen={popoverOpened}
                    align="start"
                    onClickOutside={closePopoverOnClickOutside}
                    content={popoverContent()}
                >
                    <button
                        type="button"
                        className="chat-participant chat-participant--solid"
                        onClick={() => setPopoverOpened(!popoverOpened)}
                    >
                        <i className="icon-uEA0C-gear" />
                    </button>
                </Popover>
            )}
            {(isBidChat
                ? props.info?.canLeaveChat
                : bidUsers.users?.length === 0 && bidUsers.groups?.length === 0 && props.isParticipant) && (
                <Tooltip placement="bottomRight" overlay="Выйти из чата">
                    <div className="chat-participant chat-participant--solid" onClick={leaveFromChatHandler}>
                        <i className="icon-uEA24-exit" />
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
